// src/apolloClient.js
import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';

// Assuming 'rangeHeadlessApi' is globally available or imported correctly
const { apiEndpoint, multiKeyConfig } = rangeHeadlessApi || { apiEndpoint: 'YOUR_FALLBACK_ENDPOINT', multiKeyConfig: [] };
const GRAPHQL_ENDPOINT = apiEndpoint;

// --- HTTP Link ---
const httpLink = new HttpLink({
    uri: GRAPHQL_ENDPOINT,
});

// --- Sentry Error Link ---
const sentryLink = onError(({ graphQLErrors, networkError, operation, forward, response }) => {
    let transaction;

    try {
        // Create a transaction for this GraphQL operation
        transaction = Sentry.startTransaction({
            name: `GraphQL ${operation.operationName || 'Query'}`,
            op: 'graphql',
            tags: {
                'graphql.operation_name': operation.operationName,
                'graphql.operation_type': operation.query.definitions[0]?.operation || 'query',
            },
        });

        // Capture GraphQL errors
        if (graphQLErrors) {
            graphQLErrors.forEach((error) => {
                // Check if it's a missing variable error (non-critical)
                const isMissingVariableError = error.message.includes('was not provided');
                const isNotFoundError = error.message === 'Not Found';
                
                Sentry.withScope((scope) => {
                    // Set error type
                    scope.setTag('error_type', 'graphql_error');
                    scope.setTag('manually_captured', true);
                    
                    // Set severity based on error type
                    if (isMissingVariableError || isNotFoundError) {
                        scope.setLevel('warning');
                        scope.setTag('error_category', 'initialization');
                    } else {
                        scope.setLevel('error');
                        scope.setTag('error_category', 'operational');
                    }

                    // Add operation context
                    scope.setContext('graphql_operation', {
                        operationName: operation.operationName,
                        operationType: operation.query.definitions[0]?.operation || 'query',
                        variables: operation.variables,
                    });

                    // Add error details
                    scope.setContext('graphql_error', {
                        message: error.message,
                        path: error.path,
                        extensions: error.extensions,
                        locations: error.locations,
                    });

                    // Add query information
                    scope.setExtra('query', operation.query.loc?.source.body);
                    scope.setExtra('variables', JSON.stringify(operation.variables, null, 2));

                    // Set fingerprint for better grouping
                    scope.setFingerprint([
                        'graphql',
                        operation.operationName || 'unknown',
                        error.message,
                        error.path?.join('.') || 'root'
                    ]);

                    // Only capture non-initialization errors
                    if (!isMissingVariableError) {
                        Sentry.captureException(new Error(`GraphQL Error: ${error.message}`), {
                            contexts: {
                                graphql: {
                                    operationName: operation.operationName,
                                    operationType: operation.query.definitions[0]?.operation || 'query',
                                }
                            }
                        });
                    } else {
                        // For initialization errors, use captureMessage with warning level
                        Sentry.captureMessage(`GraphQL Initialization: ${error.message}`, 'warning');
                    }
                });
            });
        }

        // Capture network errors (these are more critical)
        if (networkError) {
            Sentry.withScope((scope) => {
                // Network errors are typically more serious
                scope.setLevel('error');
                scope.setTag('error_type', 'graphql_network_error');
                scope.setTag('manually_captured', true);
                scope.setTag('error_category', 'network');

                // Add operation context
                scope.setContext('graphql_operation', {
                    operationName: operation.operationName,
                    operationType: operation.query.definitions[0]?.operation || 'query',
                    variables: operation.variables,
                    endpoint: GRAPHQL_ENDPOINT,
                });

                // Add network error details
                scope.setContext('network_error', {
                    statusCode: networkError.statusCode,
                    message: networkError.message,
                    name: networkError.name,
                    response: networkError.response,
                });

                // Add query information
                scope.setExtra('query', operation.query.loc?.source.body);
                scope.setExtra('variables', JSON.stringify(operation.variables, null, 2));

                // Set fingerprint
                scope.setFingerprint([
                    'graphql',
                    'network',
                    operation.operationName || 'unknown',
                    networkError.statusCode?.toString() || 'unknown'
                ]);

                // Capture the error
                Sentry.captureException(networkError);
            });
        }
    } catch (sentryError) {
        // If Sentry itself throws an error, log it but don't break the app
        console.error('Error in Sentry error link:', sentryError);
    } finally {
        // Safely finish the transaction
        if (transaction && typeof transaction.finish === 'function') {
            try {
                transaction.finish();
            } catch (finishError) {
                console.error('Error finishing Sentry transaction:', finishError);
            }
        }
    }
});

// --- Retry Link ---
const retryLink = new RetryLink({
    delay: {
        initial: 300,
        max: Infinity,
        jitter: true
    },
    attempts: {
        max: 3, // Retry up to 2 additional times (3 total attempts)
        retryIf: (error, _operation) => {
            // Don't retry on 429 (rate limit) errors
            if (error?.statusCode === 429) return false;

            // Don't retry on 4xx errors except 429
            if (error?.statusCode >= 400 && error?.statusCode < 500) return false;

            // Retry on network errors and 5xx errors
            return !!error;
        }
    }
});

// --- Context Link (for x-rh-keyindex if needed) ---
const contextLink = setContext((graphqlRequest, { headers }) => {
    const newHeaders = { ...headers };
    if (multiKeyConfig && Array.isArray(multiKeyConfig) && multiKeyConfig.length > 0) {
        const variables = graphqlRequest.variables || {};
        const retailerIdFromQuery = variables.retailerId;
        let keyIndex = 0;
        if (retailerIdFromQuery) {
            for (let i = 0; i < multiKeyConfig.length; i++) {
                const retailersInThisKeyGroup = multiKeyConfig[i];
                if (Array.isArray(retailersInThisKeyGroup) && retailersInThisKeyGroup.some(r => r && r.id === retailerIdFromQuery)) {
                    keyIndex = i;
                    break;
                }
            }
        }
        newHeaders['x-rh-keyindex'] = keyIndex.toString();
    }

    // Add x-rh-order header for order queries
    const operationName = graphqlRequest.operationName;
    if (operationName === 'OrdersByOrderNumber') {
        newHeaders['x-rh-order'] = 'true';
    }

    return { headers: newHeaders };
});

// --- Performance Monitoring Link ---
const performanceLink = setContext((operation, { headers }) => {
    // Add breadcrumb for GraphQL operations
    Sentry.addBreadcrumb({
        category: 'graphql',
        message: `${operation.operationName || 'GraphQL Operation'}`,
        level: 'info',
        data: {
            operationType: operation.query.definitions[0]?.operation || 'query',
            operationName: operation.operationName,
            variables: operation.variables,
        },
    });

    return { headers };
});

const link = from([
    sentryLink,      // Error handling first
    performanceLink, // Performance monitoring
    retryLink,       // Then retry logic
    contextLink,     // Then add headers
    httpLink         // Finally make the request
]);

// --- Apollo Client Cache Configuration ---
const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                // Menu queries - network-only for critical real-time data
                menu: {
                    keyArgs: ["retailerId", "filter", "menuType", "sort"],
                    merge(existing, incoming, { args, variables }) {
                        if (!incoming) return existing || {};

                        const merged = { ...(existing || {}) };
                        if (incoming.__typename) merged.__typename = incoming.__typename;

                        const queryName = variables?.queryName || (args?.operation?.name?.value) || '';
                        const isSpecialsQuery = queryName === 'MenuBySpecialsSection';

                        if (isSpecialsQuery) {
                            if (incoming.products) {
                                merged.productsCount = incoming.products.length;
                            } else if (!existing?.productsCount && incoming.products === null) {
                                merged.productsCount = 0;
                            }
                        } else if (typeof incoming.productsCount === 'number') {
                            merged.productsCount = incoming.productsCount;
                        } else if (existing && typeof existing.productsCount === 'number') {
                            merged.productsCount = existing.productsCount;
                        }

                        if (incoming.brands) merged.brands = incoming.brands;
                        if (incoming.weights) merged.weights = incoming.weights;
                        if (incoming.tags) merged.tags = incoming.tags;

                        const existingProducts = existing?.products ? existing.products.slice(0) : [];
                        const incomingProducts = incoming?.products || [];

                        if (incomingProducts.length > 0) {
                            if (args?.pagination?.offset != null && args.pagination.offset > 0) {
                                let newProductList = existingProducts.slice(0);
                                const requiredLength = args.pagination.offset + incomingProducts.length;
                                while (newProductList.length < requiredLength) {
                                    newProductList.push(undefined);
                                }
                                for (let i = 0; i < incomingProducts.length; ++i) {
                                    newProductList[args.pagination.offset + i] = incomingProducts[i];
                                }
                                merged.products = newProductList.filter(p => p !== undefined);
                            } else {
                                merged.products = incomingProducts;
                            }
                        } else if (args?.pagination?.offset === 0 && incomingProducts.length === 0) {
                            merged.products = [];
                        } else if (!existing?.products) {
                            merged.products = [];
                        }
                        return merged;
                    },
                },

                // Checkout queries
                checkout: {
                    keyArgs: ["id"],
                    merge(existing, incoming) {
                        // Always prefer incoming data for checkout
                        return incoming;
                    },
                    read(existing, { args, toReference }) {
                        if (existing) return existing;
                        // Try to read from normalized cache
                        if (args?.id) {
                            return toReference({
                                __typename: 'Checkout',
                                id: args.id
                            });
                        }
                        return existing;
                    }
                },

                // Product queries - cache-first with short TTL
                product: {
                    keyArgs: ["id", "retailerId"],
                    merge: true
                },

                // Specials - network-only (time-sensitive)
                specials: {
                    keyArgs: ["retailerId"],
                    read() {
                        return undefined; // Force network fetch
                    }
                },

                // Retailer info - cache-and-network (changes infrequently)
                retailer: {
                    keyArgs: ["id"],
                    merge: true
                },

                // Order
                orders: {
                    keyArgs: ["retailerId", "orderNumber"],
                    merge(existing, incoming) {
                        return incoming || existing;
                    }
                }
            }
        },
        // Checkout/Cart - optimistic updates with cache-first
        Checkout: {
            keyFields: ["id"],
            fields: {
                items: {
                    merge(existing = [], incoming) {
                        return incoming;
                    }
                }
            }
        },

        CheckoutItem: {
            keyFields: ["id"],
            fields: {
                // Ensure product references are properly cached
                product: {
                    merge: true
                }
            }
        },

        Product: {
            keyFields: ["id"],
        },
        Brand: {
            keyFields: ["id"],
        },
        ProductVariant: {
            keyFields: ["id"]
        },
        Order: {
            keyFields: ["id"],
        }
    }
});

// Configure default fetch policies
const client = new ApolloClient({
    link: link,
    cache: cache,
    connectToDevTools: process.env.NODE_ENV === 'development',
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
            nextFetchPolicy: 'cache-first',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
        },
        mutate: {
            errorPolicy: 'all',
        }
    }
});

// Helper function to manually log successful queries (optional)
export const logQuerySuccess = (operationName, data, variables) => {
    Sentry.addBreadcrumb({
        category: 'graphql.success',
        message: `${operationName} completed successfully`,
        level: 'info',
        data: {
            operationName,
            variables,
            resultSize: JSON.stringify(data).length,
        },
    });
};

// Helper function to manually capture slow queries (optional)
export const logSlowQuery = (operationName, duration, variables) => {
    if (duration > 2000) { // Log queries slower than 2 seconds
        Sentry.captureMessage(`Slow GraphQL Query: ${operationName}`, {
            level: 'warning',
            tags: {
                'graphql.slow_query': true,
                'graphql.operation_name': operationName,
            },
            extra: {
                duration,
                variables,
            },
        });
    }
};

export default client;
